import { create, StateCreator } from 'zustand';
import { Project, Contribution, ContributionStatus, ProjectComment, ProjectStatus } from '@/types/project';
import useAuthStore from './useAuthStore';
import {
  saveToStorage,
  loadFromStorage,
} from '@/lib/localStorage';

// Type for project update operations (combines Project fields with API-specific fields)
type ProjectUpdateFields = Partial<Project> & {
  coverImageUrl?: string | null;
  audioUrl?: string | null;
};

interface ProjectCache {
  [key: string]: {
    data: Project;
    timestamp: number;
  };
}

interface VotingOption {
  id: string;
  label: string;
  value: string;
  votes: number;
}

interface ProjectSettings {
  key?: VotingOption;
  bpm?: VotingOption;
  style?: VotingOption;
}

interface SimulatedProjectData {
  duration: string;
  teamSize: number;
  settings: ProjectSettings;
}

interface ProjectStore {
  projects: Project[];
  joinedProjects: string[];
  activeProject: Project | null;
  loading: boolean;
  error: string | null;
  cache: ProjectCache;
  sortBy: SortByType;

  setProjects: (projects: Project[]) => void;
  addProject: (projectData: {
    name: string;
    description?: string;
    type?: string;
    isPublic?: boolean;
    coverImageUrl?: string;
    youtubeUrl?: string;
    goals?: string;
    status?: ProjectStatus;
    duration?: {
        start?: string;
        end?: string;
    };
    isClientSideSimulated?: boolean;
    simulatedData?: SimulatedProjectData;
  }) => Promise<Project | null>;
  updateProject: (projectId: string, updates: ProjectUpdateFields) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>; 
  archiveProject: (projectId: string) => void;
  setActiveProject: (project: Project | null) => void;
  getProject: (projectId: string) => Project | null;
  joinProject: (projectId: string) => Promise<void>;
  leaveProject: (projectId: string) => Promise<void>;
  hasJoined: (projectId: string) => boolean;
  addComment: (projectId: string, comment: ProjectComment) => void;
  addContribution: (projectId: string, contribution: Contribution) => void;
  updateContribution: (projectId: string, contributionId: string, updates: Partial<Contribution>) => void;
  voteContribution: (projectId: string, contributionId: string, increment: boolean) => void;
  mergeContribution: (projectId: string, contributionId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSortBy: (sortBy: SortByType) => void;
  fetchProjects: () => Promise<void>;
  fetchProjectById: (projectId: string) => Promise<Project | null>;
}

type SortByType = 'recent' | 'popular' | 'alphabetical';

const CACHE_DURATION = 5 * 60 * 1000; 
const MAX_CACHE_SIZE = 100;
const JOINED_PROJECTS_KEY = 'user-joined-projects';

const loadJoinedProjectsFromStorage = (): string[] => {
  const joined = loadFromStorage<string[]>(JOINED_PROJECTS_KEY, []) || [];
  return joined;
};

const createProjectStore: StateCreator<ProjectStore, [], [], ProjectStore> = (
  set: (partial: ProjectStore | Partial<ProjectStore> | ((state: ProjectStore) => ProjectStore | Partial<ProjectStore>), replace?: boolean | undefined) => void,
  get: () => ProjectStore
) => {
  const initialJoinedProjects = loadJoinedProjectsFromStorage();

  const createClientSideProject = (simulatedData: SimulatedProjectData): Project => {
    const currentUser = useAuthStore.getState().user;
    const now = new Date().toISOString();
    const projectId = `sim-project-${Date.now()}`;
    const teamName = simulatedData.teamSize === 2 ? 'Duo' : simulatedData.teamSize === 3 ? 'Trio' : 'Squad';

    return {
      id: projectId,
      title: `My New ${simulatedData.duration.replace('_', ' ')} ${teamName} Project`,
      description: `A collaborative project for a ${simulatedData.duration.replace('_', ' ')} team of ${teamName}.`,
      owner: {
        id: currentUser?.id || 'anonymous',
        name: currentUser?.username || 'Anonymous',
        avatar: currentUser?.profilePictureUrl || currentUser?.avatar || ''
      },
      status: 'draft',
      isPublic: true,
      isContributor: true,
      coverImage: 'https://images.unsplash.com/photo-1497215729967-00f28249d0f2?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', // Placeholder
      createdAt: now,
      updatedAt: now,
      contributors: [
        { id: currentUser?.id || 'anonymous', name: currentUser?.username || 'Anonymous', avatar: currentUser?.profilePictureUrl || currentUser?.avatar || '', role: 'owner' },
        // You can add mock collaborators here based on simulatedData.teamSize
      ],
      comments: [],
      workspace: { mainContent: '', contributions: [], lastUpdated: now },
      duration: {
        start: now,
        end: undefined,
      },
      goals: `Create a ${simulatedData.duration.replace('_', ' ')} project with a ${teamName} team. Key: ${simulatedData.settings?.key?.label}, BPM: ${simulatedData.settings?.bpm?.label}, Style: ${simulatedData.settings?.style?.label}`,
      stats: { likes: 0, comments: 0, views: 0 },
    };
  };

  return {
    projects: [], 
    joinedProjects: initialJoinedProjects,
    activeProject: null,
    loading: false,
    error: null,
    cache: {},
    sortBy: 'recent',

    setProjects: (projects) => {
      const sortedProjects = [...projects].sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      }).slice(0, MAX_CACHE_SIZE);
      const cache = sortedProjects.reduce((acc, project) => {
        acc[project.id] = { data: project, timestamp: Date.now() };
        return acc;
      }, {} as ProjectCache);
      set({ projects: sortedProjects, cache });
    },

    addProject: async (projectData) => {
      set({ loading: true, error: null });
      try {
        let newProject: Project;

        if (projectData.isClientSideSimulated && projectData.simulatedData) {
          newProject = createClientSideProject(projectData.simulatedData);
        } else {
          // Original backend call logic
          const response = await fetch('/api/projects', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              ...projectData,
              status: projectData.status || 'draft' // Default to draft if not specified
            }),
          });
          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create project.');
          }
          const backendProject = data.project;
          const currentUser = useAuthStore.getState().user;
          console.log('[ProjectStore] Creating project with backend status:', backendProject.status);
          newProject = {
            id: backendProject.projectId,
            title: backendProject.name,
            description: backendProject.description,
            owner: {
              id: backendProject.owner?.userId || backendProject.ownerUserId,
              name: backendProject.owner?.username || currentUser?.username || 'Owner',
              avatar: backendProject.owner?.avatarUrl || currentUser?.avatar || ''
            },
            status: backendProject.status as ProjectStatus,
            isPublic: backendProject.isPublic,
            isContributor: true,
            coverImage: backendProject.coverImageUrl || '',
            youtubeUrl: backendProject.youtubeUrl || projectData.youtubeUrl, // Include YouTube URL from backend or original data
            createdAt: new Date(backendProject.createdAt * 1000).toISOString(),
            updatedAt: new Date(backendProject.updatedAt * 1000).toISOString(),
            contributors: backendProject.contributors || [{ id: backendProject.ownerUserId, name: currentUser?.username || 'Owner', avatar: currentUser?.avatar || '', role: 'owner' }],
            comments: backendProject.comments || [],
            workspace: backendProject.workspace || { mainContent: '', contributions: [], lastUpdated: new Date(backendProject.updatedAt * 1000).toISOString() },
            duration: {
              start: backendProject.startDate ? new Date(backendProject.startDate * 1000).toISOString() : new Date(backendProject.createdAt * 1000).toISOString(),
              end: backendProject.endDate ? new Date(backendProject.endDate * 1000).toISOString() : undefined,
            },
            goals: backendProject.goals,
            stats: backendProject.stats,
          };
        }

        set((state) => {
          // Don't add owned projects to joinedProjects
          const updatedJoinedProjects = newProject.owner.id === useAuthStore.getState().user?.id
            ? state.joinedProjects
            : (state.joinedProjects.includes(newProject.id) ? state.joinedProjects : [...state.joinedProjects, newProject.id]);

          if (updatedJoinedProjects !== state.joinedProjects) {
            saveToStorage(JOINED_PROJECTS_KEY, updatedJoinedProjects);
          }

          return {
            projects: [newProject, ...state.projects].slice(0, MAX_CACHE_SIZE),
            activeProject: newProject,
            joinedProjects: updatedJoinedProjects,
            loading: false, error: null
          };
        });
        return newProject;
      } catch (error: unknown) {
        set({ loading: false, error: (error as Error).message || 'Failed to create project.' });
        return null;
      }
    },

    updateProject: async (projectId, updates) => {
      set({ loading: true, error: null });
      try {
        const response = await fetch(`/api/projects/${projectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updates),
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || `Failed to update project ${projectId}.`);
        }
        const backendUpdatedProject = data.project;
        const currentUser = useAuthStore.getState().user;
        const updatedProjectFrontend: Project = {
          ...(get().projects.find(p => p.id === projectId) || get().activeProject),
          ...updates,
          id: backendUpdatedProject.projectId,
          title: backendUpdatedProject.name,
          description: backendUpdatedProject.description,
          owner: {
            id: backendUpdatedProject.owner?.userId || backendUpdatedProject.ownerUserId,
            name: backendUpdatedProject.owner?.username || currentUser?.username || 'Owner',
            avatar: backendUpdatedProject.owner?.avatarUrl || currentUser?.avatar || '',
          },
          status: updates.status || (backendUpdatedProject.status as ProjectStatus),
          isPublic: updates.isPublic !== undefined ? updates.isPublic : backendUpdatedProject.isPublic,
          isContributor: backendUpdatedProject.isContributor,
          coverImage: backendUpdatedProject.coverImageUrl || '',
          audioUrl: backendUpdatedProject.audioUrl || '',
          youtubeUrl: backendUpdatedProject.youtubeUrl || '',
          createdAt: new Date(backendUpdatedProject.createdAt * 1000).toISOString(),
          updatedAt: new Date(backendUpdatedProject.updatedAt * 1000).toISOString(),
          contributors: backendUpdatedProject.contributors || (get().projects.find(p => p.id === projectId) || get().activeProject)?.contributors || [],
          comments: backendUpdatedProject.comments || (get().projects.find(p => p.id === projectId) || get().activeProject)?.comments || [],
          workspace: backendUpdatedProject.workspace || (get().projects.find(p => p.id === projectId) || get().activeProject)?.workspace || { mainContent: '', contributions: [], lastUpdated: new Date(backendUpdatedProject.updatedAt * 1000).toISOString() },
          duration: {
            start: backendUpdatedProject.duration?.start || (get().projects.find(p => p.id === projectId) || get().activeProject)?.duration?.start || new Date(backendUpdatedProject.createdAt * 1000).toISOString(),
            end: backendUpdatedProject.duration?.end,
          },
          goals: backendUpdatedProject.goals || (get().projects.find(p => p.id === projectId) || get().activeProject)?.goals,
          stats: backendUpdatedProject.stats || (get().projects.find(p => p.id === projectId) || get().activeProject)?.stats,
        };
        set((state) => {
          const updatedProjects = state.projects.map(p => p.id === projectId ? updatedProjectFrontend : p);
          const updatedCache = { ...state.cache, [projectId]: { data: updatedProjectFrontend, timestamp: Date.now() } };
          const updatedActiveProject = state.activeProject && state.activeProject.id === projectId ? updatedProjectFrontend : state.activeProject;
          return {
            projects: updatedProjects, cache: updatedCache, activeProject: updatedActiveProject, loading: false, error: null,
          };
        });
      } catch (error: unknown) {
        set({ loading: false, error: (error as Error).message || `Failed to update project ${projectId}.` });
      }
    },

    deleteProject: async (projectId) => { 
      try {
        // Call the API to delete the project
        const response = await fetch(`/api/projects/${projectId}`, {
          method: 'DELETE',
          credentials: 'include',
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to delete project');
        }

        // Update local state after successful deletion
        set((state) => {
          const updatedProjects = state.projects.filter(p => p.id !== projectId);
          const updatedJoinedProjects = state.joinedProjects.filter(id => id !== projectId);
          const updatedCache = { ...state.cache };
          delete updatedCache[projectId];
          saveToStorage(JOINED_PROJECTS_KEY, updatedJoinedProjects);
          
          // Clear conversation storage for this project
          localStorage.removeItem(`project-conversation-${projectId}`);
          
          const updatedActiveProject = state.activeProject && state.activeProject.id === projectId ? null : state.activeProject;
          return { projects: updatedProjects, joinedProjects: updatedJoinedProjects, cache: updatedCache, activeProject: updatedActiveProject };
        });

        console.log('Project deleted successfully');
      } catch (error) {
        console.error('Error deleting project:', error);
        set({ error: (error as Error).message || 'Failed to delete project' });
        throw error; // Re-throw so the UI can handle it
      }
    },

    archiveProject: (projectId) => { 
      get().updateProject(projectId, { status: 'archived' }); 
    },

    setActiveProject: (project: Project | null) => set({ activeProject: project }),

    getProject: (projectId: string) => {
      const state = get();
      if (state.activeProject && state.activeProject.id === projectId) return state.activeProject;
      const projectInArray = state.projects.find(p => p.id === projectId);
      if (projectInArray) {
        if (!state.cache[projectId] || (Date.now() - state.cache[projectId].timestamp > CACHE_DURATION)) {
          set(prevState => ({ cache: { ...prevState.cache, [projectId]: { data: projectInArray, timestamp: Date.now() } } }));
        }
        return projectInArray;
      }
      const cachedEntry = state.cache[projectId];
      if (cachedEntry && (Date.now() - cachedEntry.timestamp < CACHE_DURATION)) return cachedEntry.data;
      return null;
    },

    fetchProjectById: async (projectId: string): Promise<Project | null> => {
      // Always fetch fresh data from backend to ensure we have latest project status
      // Don't use cached data as project status may have changed (draft->active, active->archived, etc.)
      set({ loading: true, error: null });
      try {
        const response = await fetch(`/api/projects/${projectId}`, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || `Failed to fetch project ${projectId}.`);
        const backendProject = data.project;
        const currentUser = useAuthStore.getState().user;
        const fetchedProject: Project = {
          id: backendProject.projectId,
          title: backendProject.name,
          description: backendProject.description,
          owner: {
            id: backendProject.owner?.userId || backendProject.ownerUserId,
            name: backendProject.owner?.username || currentUser?.username || 'Owner',
            avatar: backendProject.owner?.avatarUrl || currentUser?.avatar || ''
          },
          status: backendProject.status as ProjectStatus,
          isPublic: backendProject.isPublic,
          isContributor: backendProject.isContributor,
          coverImage: backendProject.coverImageUrl || '',
          audioUrl: backendProject.audioUrl || '', // Include audio URL from backend
          youtubeUrl: backendProject.youtubeUrl, // Include YouTube URL from backend
          createdAt: new Date(backendProject.createdAt * 1000).toISOString(),
          updatedAt: new Date(backendProject.updatedAt * 1000).toISOString(),
          contributors: backendProject.contributors || [{ id: backendProject.ownerUserId, name: currentUser?.username || 'Owner', avatar: currentUser?.avatar || '', role: 'owner' }],
          comments: backendProject.comments || [],
          workspace: backendProject.workspace || { mainContent: '', contributions: [], lastUpdated: new Date(backendProject.updatedAt * 1000).toISOString() },
          duration: { start: backendProject.duration?.start || new Date(backendProject.createdAt * 1000).toISOString(), end: backendProject.duration?.end },
          goals: backendProject.goals,
          stats: backendProject.stats,
        };
        set((state) => {
          const projectExists = state.projects.some(p => p.id === fetchedProject.id);
          const updatedProjects = projectExists ? state.projects.map(p => p.id === fetchedProject.id ? fetchedProject : p) : [fetchedProject, ...state.projects].slice(0, MAX_CACHE_SIZE);
          const updatedCache = { ...state.cache, [fetchedProject.id]: { data: fetchedProject, timestamp: Date.now() } };
          if (Object.keys(updatedCache).length > MAX_CACHE_SIZE) {
            const oldestEntryKey = Object.entries(updatedCache).sort(([, a], [, b]) => a.timestamp - b.timestamp)[0]?.[0];
            if (oldestEntryKey) delete updatedCache[oldestEntryKey];
          }
          return { projects: updatedProjects, activeProject: fetchedProject, cache: updatedCache, loading: false, error: null };
        });
        return fetchedProject;
      } catch (error: unknown) {
        set({ loading: false, error: (error as Error).message || `Failed to fetch project ${projectId}.` });
        if (get().activeProject?.id === projectId) set({ activeProject: null });
        return null;
      }
    },

    joinProject: async (projectId: string) => {
      set({ loading: true, error: null }); 
      try {
        const response = await fetch(`/api/projects/${projectId}/join`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        const data = await response.json();
        if (!response.ok || !data.success) {
          if (response.status === 200 && data.message === 'User is already a member of this project.') {
            // User is already a member, local state will be updated by fetchProjectById
          } else {
            throw new Error(data.message || `Failed to join project ${projectId}.`);
          }
        }
        // Optimistically update joinedProjects list
        set((state) => {
          const newJoinedProjects = state.joinedProjects.includes(projectId) ? state.joinedProjects : [...state.joinedProjects, projectId];
          if (!state.joinedProjects.includes(projectId)) {
            saveToStorage(JOINED_PROJECTS_KEY, newJoinedProjects);
          }
          // loading and error will be set by fetchProjectById
          return { joinedProjects: newJoinedProjects }; 
        });
        await get().fetchProjectById(projectId); 
      } catch (error: unknown) {
        set({ loading: false, error: (error as Error).message || `Failed to join project ${projectId}.` });
      }
    },

    leaveProject: async (projectId: string) => {
      const originalProjects = get().projects;
      const originalJoinedProjects = get().joinedProjects;
      const originalActiveProject = get().activeProject;

      // 1. Optimistic UI update
      set((state) => {
        const newJoinedProjects = state.joinedProjects.filter(id => id !== projectId);
        saveToStorage(JOINED_PROJECTS_KEY, newJoinedProjects); // Persist optimistic joined state

        const updatedProjects = state.projects.map(p =>
          p.id === projectId ? { ...p, isContributor: false } : p
        );

        let updatedActiveProject = state.activeProject;
        if (state.activeProject && state.activeProject.id === projectId) {
          updatedActiveProject = { ...state.activeProject, isContributor: false };
        }

        return {
          joinedProjects: newJoinedProjects,
          projects: updatedProjects,
          activeProject: updatedActiveProject,
          loading: true, // Indicate an operation is in progress
          error: null,
        };
      });

      try {
        // 2. Call backend API
        const response = await fetch(`/api/projects/${projectId}/leave`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || `Failed to leave project ${projectId}.`);
        }

        // 3. On success, fetch canonical state to ensure consistency.
        // 3. On success, fetch the specific project's canonical state to ensure consistency for the active view.
        //    This will update activeProject with the latest data (e.g., isContributor: false).
        //    If the project is no longer accessible at all (e.g., private and user removed),
        //    fetchProjectById will handle setting activeProject to null.
        await get().fetchProjectById(projectId);

        //    Also, refresh the general list of projects in the background.
        await get().fetchProjects();
        //    Both fetchProjectById and fetchProjects handle their own loading/error states,
        //    and the final state will reflect the completion of both.
      } catch (error: unknown) {
        // 4. On failure, roll back optimistic updates
        set({
          projects: originalProjects,
          joinedProjects: originalJoinedProjects,
          activeProject: originalActiveProject,
          loading: false,
          error: (error as Error).message || `Failed to leave project ${projectId}.`,
        });
        // Re-save original joined projects to storage
        saveToStorage(JOINED_PROJECTS_KEY, originalJoinedProjects);
      }
    },

    hasJoined: (projectId: string) => get().joinedProjects.includes(projectId),

    addComment: (projectId, comment) => {
      const project = get().getProject(projectId);
      if (!project) return;
      const comments = project.comments ? [...project.comments] : [];
      comments.push(comment);
      get().updateProject(projectId, { comments });
    },

    addContribution: (projectId, contribution) => {
      const project = get().getProject(projectId);
      if (!project || !project.workspace) return;
      const contributions = project.workspace.contributions ? [...project.workspace.contributions] : [];
      const formattedContribution = { ...contribution, createdAt: typeof contribution.createdAt === 'object' ? (contribution.createdAt as Date).toISOString() : contribution.createdAt, updatedAt: typeof contribution.updatedAt === 'object' ? (contribution.updatedAt as Date).toISOString() : contribution.updatedAt };
      contributions.push(formattedContribution);
      const updatedWorkspace = { ...project.workspace, contributions, lastUpdated: new Date().toISOString() };
      get().updateProject(projectId, { workspace: updatedWorkspace });
    },

    updateContribution: (projectId, contributionId, updates) => {
      const project = get().getProject(projectId);
      if (!project || !project.workspace?.contributions) return;
      const contributions = [...project.workspace.contributions];
      const contributionIndex = contributions.findIndex(c => c.id === contributionId);
      if (contributionIndex === -1) return;
      const updatedContribution = { ...contributions[contributionIndex], ...updates, updatedAt: new Date().toISOString() };
      contributions[contributionIndex] = updatedContribution;
      const updatedWorkspace = { ...project.workspace, contributions, lastUpdated: new Date().toISOString() };
      get().updateProject(projectId, { workspace: updatedWorkspace });
    },

    voteContribution: (projectId, contributionId, increment) => {
      const project = get().getProject(projectId);
      if (!project || !project.workspace?.contributions) return;
      const contributions = project.workspace.contributions.map(c => c.id === contributionId ? { ...c, votes: c.votes + (increment ? 1 : -1), hasVoted: increment } : c);
      const updatedWorkspace = { ...project.workspace, contributions, lastUpdated: new Date().toISOString() };
      get().updateProject(projectId, { workspace: updatedWorkspace });
    },

    mergeContribution: (projectId, contributionId) => {
      const project = get().getProject(projectId);
      if (!project || !project.workspace?.contributions) return;
      let mergedContent = project.workspace.mainContent;
      const contributions = project.workspace.contributions.map(c => {
        if (c.id === contributionId) {
          mergedContent += `\n\n--- Contribution from ${c.author} (Merged on ${new Date().toLocaleDateString()}) ---\n${c.content}`;
          return { ...c, status: 'merged' as ContributionStatus, mergedAt: new Date().toISOString() };
        }
        return c;
      });
      const updatedWorkspace = { ...project.workspace, mainContent: mergedContent, contributions, lastUpdated: new Date().toISOString() };
      get().updateProject(projectId, { workspace: updatedWorkspace });
    },

    setLoading: (loading: boolean) => set({ loading }),
    setError: (error: string | null) => set({ error }),
    setSortBy: (sortBy: SortByType) => set({ sortBy }),

    fetchProjects: async () => {
      set({ loading: true, error: null });
      try {
        const response = await fetch('/api/me/projects', { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch user projects.');
        const currentUser = useAuthStore.getState().user;
        const fetchedProjects: Project[] = data.projects.map((p: {
          projectId: string; name: string; description: string | null; ownerUserId: string; ownerUsername?: string;
          projectType: string; status: ProjectStatus | string; isPublic: boolean | number; userRoleInProject?: string;
          coverImageUrl: string | null; audioUrl?: string; createdAt: number; updatedAt: number;
          contributors?: Array<{ userId: string; user?: { username?: string; avatarUrl?: string }; role: string }>;
          comments?: ProjectComment[];
          workspace?: { mainContent: string; contributions: Contribution[]; lastUpdated: string; };
          startDate?: number; endDate?: number;
          goals?: string | null;
          stats?: { likes?: number; comments?: number; views?: number; };
        }) => ({
          id: p.projectId,
          title: p.name,
          description: p.description || '',
          owner: { 
            id: p.ownerUserId, 
            name: p.ownerUsername || (p.ownerUserId === currentUser?.id ? currentUser?.username : 'Owner'),
            avatar: (p.ownerUserId === currentUser?.id ? currentUser?.avatar : '') || '' 
          },
          type: p.projectType || 'other',
          status: p.status as ProjectStatus,
          isPublic: !!p.isPublic,
          isContributor: p.userRoleInProject !== undefined,
          coverImage: p.coverImageUrl || '',
          audioUrl: p.audioUrl || '',
          createdAt: new Date(p.createdAt * 1000).toISOString(),
          updatedAt: new Date(p.updatedAt * 1000).toISOString(),
          contributors: p.contributors || (p.ownerUserId === currentUser?.id ? [{id: currentUser.id, name: currentUser.username || 'Owner', avatar: currentUser.avatar || '', role: 'owner'}] : []),
          comments: p.comments || [],
          workspace: p.workspace || { mainContent: '', contributions: [], lastUpdated: new Date(p.updatedAt * 1000).toISOString() },
          duration: { start: p.startDate ? new Date(p.startDate * 1000).toISOString() : new Date(p.createdAt * 1000).toISOString(), end: p.endDate ? new Date(p.endDate * 1000).toISOString() : undefined },
          goals: p.goals,
          stats: p.stats,
        }));

        get().setProjects(fetchedProjects);
        const newJoinedProjects = fetchedProjects.filter(p => p.isContributor && p.owner.id !== currentUser?.id).map(p => p.id);
        set({ joinedProjects: newJoinedProjects, loading: false, error: null });
        saveToStorage(JOINED_PROJECTS_KEY, newJoinedProjects);
      } catch (error: unknown) {
        set({ loading: false, error: (error as Error).message || 'Failed to fetch projects.' });
      }
    },
  };
};

const useProjectStore = create(createProjectStore);

export default useProjectStore;